import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@components/ui/Card';
import Button from '@components/ui/Button';
import SearchInput from '@components/ui/SearchInput';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import EmptyState from '@components/ui/EmptyState';
import ErrorState from '@components/ui/ErrorState';
import './Companies.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Company {
  id: string;
  company_name: string;
  website: string | null;
  people_count: number;
  projects_count: number;
  created_at: string;
  updated_at: string;
}

interface CompaniesResponse {
  success: boolean;
  data: Company[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

const Companies: React.FC = () => {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('q', debouncedSearch);
      params.set('page_size', '50');

      const res = await fetch(`/api/companies?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: CompaniesResponse = await res.json();
      if (!json.success) throw new Error('API returned failure');
      setCompanies(json.data);
      setTotal(json.pagination.total);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
      setError('Failed to load companies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="companies-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Companies</h1>
          {!loading && !error && (
            <span className="page-count">
              {total} {total === 1 ? 'company' : 'companies'}
            </span>
          )}
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate('/companies/new')}
        >
          + New company
        </Button>
      </div>

      {/* Search */}
      <div className="companies-filters">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name or website…"
          className="companies-search"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="companies-loading">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <Card>
          <ErrorState message={error} onRetry={fetchCompanies} />
        </Card>
      ) : companies.length === 0 ? (
        <Card>
          <EmptyState
            title={debouncedSearch ? 'No companies match your search' : 'No companies yet'}
            message={
              debouncedSearch
                ? 'Try adjusting your search.'
                : 'Companies are created when you add a person, or you can add one directly.'
            }
            action={
              !debouncedSearch ? (
                <Button variant="primary" size="sm" onClick={() => navigate('/companies/new')}>
                  + New company
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="companies-list">
          {companies.map((company) => (
            <button
              key={company.id}
              className="company-row"
              onClick={() => navigate(`/companies/${company.id}`)}
              type="button"
            >
              <div className="company-row-main">
                <span className="company-name">{company.company_name}</span>
              </div>
              <div className="company-row-meta">
                {company.website && (
                  <span className="company-website">{company.website}</span>
                )}
                <span className="company-counts">
                  {company.people_count} {company.people_count === 1 ? 'person' : 'people'}
                  {' · '}
                  {company.projects_count} {company.projects_count === 1 ? 'project' : 'projects'}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Companies;
